import { ReactNode } from "react";

type SpinnerProp = {
  size: number;
  color?: string;
  compact?: boolean;
};

export const Spinner = ({ size, color, compact }: SpinnerProp) => {
  const spinner = (
    <div
      className="inline-block animate-spin rounded-full border-t-4 border-solid bg-transparent"
      style={{
        height: size,
        width: size,
        borderColor: color ?? "var(--primary-color)",
      }}
    />
  );

  if (compact) {
    return spinner;
  }

  return (
    <div className="flex h-full w-full items-center justify-center pb-[5px] pl-[64px] pr-[64px] pt-[5px]">
      {spinner}
    </div>
  );
};

/**
 * Cross-fades button content with a centered {@link Spinner} based on `loading`.
 * Both layers stay laid out (content keeps the button's width) and swap via a
 * springy opacity+scale transition, so the spinner glides in as the label glides
 * out and back — never a jarring cut or a size jump.
 */
export const SpinnerSwap = ({
  loading,
  spinnerColor,
  spinnerSize = 18,
  children,
}: {
  loading: boolean;
  spinnerColor?: string;
  spinnerSize?: number;
  children: ReactNode;
}) => {
  const ease =
    "opacity 0.2s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)";
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          opacity: loading ? 0 : 1,
          transform: loading ? "scale(0.85)" : "scale(1)",
          transition: ease,
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        {children}
      </span>
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: loading ? 1 : 0,
          transform: loading ? "scale(1)" : "scale(0.5)",
          transition: ease,
          pointerEvents: "none",
        }}
      >
        <Spinner size={spinnerSize} color={spinnerColor} compact />
      </span>
    </span>
  );
};
